import React, { Component } from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { withStyles } from 'material-ui/styles';
import { TREE_EVENT } from 'shared/dictionaries/events';

import Dialog, { DialogContent } from 'material-ui/Dialog';
import AppBar from 'material-ui/AppBar';
import Toolbar from 'material-ui/Toolbar';
import IconButton from 'material-ui/IconButton';
import { CircularProgress } from 'material-ui/Progress';
import { Close, Magnify, ArrowLeft, FileTree } from 'mdi-material-ui';
import Slide from 'material-ui/transitions/Slide';
import TextField from 'material-ui/TextField';
import List, { ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction } from 'material-ui/List';
import NoData from 'components/NoData';
import NodePrimaryText from 'components/list/NodePrimaryText';
import NodeSecondaryText from 'components/list/NodeSecondaryText';

import moment from 'moment';
import 'moment/locale/ru';

import withAppStore from 'decorators/withAppStore';

import styles from 'styles/components/SearchDialog';

const SEARCH_DELAY = 1000;
const MIN_SEARCH_LENGTH = 2;

@withStyles(styles)
@withAppStore
@observer
export default class SearchDialog extends Component {

  searchTimeout = null;

  @observable
  isLoading = false;

  @observable
  searchTermInput = null;

  @computed
  get store() {
    return this.appStore.treeStore;
  }

  @computed
  get searchTerm() {
    return this.appStore.searchTerm;
  }
  set searchTerm(value) {
    this.appStore.searchTerm = value;
  }

  @computed
  get results() {
    return this.store.foundNodes;
  }

  @computed
  get isOpened() {
    return this.appStore.isSearchOpen;
  }

  @computed
  get isInfoEnabled() {
    const { dataStore, disableInfo } = this.appStore;
    return dataStore.isInfoSupported && !disableInfo;
  }

  @computed
  get showResults() {
    return !this.showInitialPlaceholder && !!this.results.length;
  }

  @computed
  get showNoData() {
    return !this.results.length && !!this.searchTerm && !this.isLoading && !this.appStore.hasApiError;
  }

  get showTooManyRequestsError() {
    const { hasApiError, isTooManyRequests } = this.appStore;
    return hasApiError && isTooManyRequests;
  }

  @computed
  get showInitialPlaceholder() {
    const noResults = this.store.lastSearchTerm && !this.results.length && !this.searchTerm && !this.isLoading;
    const noSearchTerm = !this.searchTerm && !this.store.lastSearchTerm;
    return noResults || noSearchTerm;
  }

  @computed
  get closeType() {
    return this.appStore.closeType;
  }

  @computed
  get searchButton() {
    return (
      <IconButton onClick={ this.handleSearch } disabled={ this.isLoading }>
        { this.isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Magnify />
        )}
      </IconButton>
    );
  }

  @computed
  get closeButton() {
    return (
      <IconButton onClick={ this.handleClose } aria-label="Close">
        { this.closeType.isClose ? <Close /> : <ArrowLeft /> }
      </IconButton>
    );
  }

  @computed
  get leftButton() {
    return this.closeType.isClose ? this.searchButton : this.closeButton;
  }

  get rightButton() {
    return this.closeType.isClose ? this.closeButton : this.searchButton;
  }

  componentWillMount() {
    if (this.searchTerm && this.results.length === 0) {
      this.handleSearch();
    }
  }

  render() {
    const { classes } = this.props;

    return (
      <Dialog
        fullScreen
        open={ this.isOpened }
        onEntered={ this.handleOpen }
        onClose={ this.handleClose }
        transition={ Transition }
      >
        <AppBar color='default' elevation={1} >
          <Toolbar>

            { this.leftButton }

            <TextField
              autoFocus
              fullWidth
              placeholder='Критерий поиска'
              value={ this.searchTerm }
              onChange={ this.handleChange }
              onKeyUp={ this.handleKeyUp }
              inputRef={ (input) => this.searchTermInput = input }
            />

            { this.rightButton }

          </Toolbar>
        </AppBar>

        <DialogContent className={ classes.content }>

          { this.showResults &&
            <List dense={ this.appStore.dense } >
              { this.results.map(node => (
                <ListItem button onClick={ () => this.handleInfoClick(node) } key={ node.id }>
                  { node.isLoading &&
                    <ListItemIcon >
                      <CircularProgress size={24} />
                    </ListItemIcon>
                  }
                  <ListItemText
                    disableTypography
                    primary={ <NodePrimaryText node={ node } /> }
                    secondary={ <NodeSecondaryText node={ node } showShorDescription /> }
                  />
                  { this.isInfoEnabled && node.hasInfo &&
                    <ListItemSecondaryAction>
                      <IconButton onClick={ () => this.handleNodeClick(node) } >
                        <FileTree />
                      </IconButton>
                    </ListItemSecondaryAction>
                  }
                </ListItem>
              ))}
            </List>
          }

          <NoData visible={ this.showNoData } text='Ничего не найдено' />
          <NoData visible={ this.showInitialPlaceholder } text='Введите критерий для поиска' />
          <NoData visible={ this.showTooManyRequestsError } text={`Превышен лимит на количество запросов. Попробуйте ${moment().add(this.appStore.retryAfter, 'seconds').endOf().fromNow()}`} />
        </DialogContent>

      </Dialog>
    );
  }

  @action.bound
  handleClose() {
    this.appStore.isSearchOpen = false;
    this.appStore.sendEvent(TREE_EVENT.SEARCH_CLOSE);
  }

  @action.bound
  handleOpen() {
    this.searchTermInput.select();
    this.searchTermInput.focus();
  }

  @action.bound
  handleChange({ target: { value } }) {
    this.searchTerm = value;
  }

  @action.bound
  handleKeyUp(event){
    if (event.keyCode === 13) {
      this.handleSearch();
    }

    if (!this.searchTerm || this.searchTerm.length < MIN_SEARCH_LENGTH || this.searchTerm === this.store.lastSearchTerm) {
      return;
    }

    if (this.searchTimeout) {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.searchTimeout = window.setTimeout(this.handleSearch, SEARCH_DELAY);
  }

  @action.bound
  handleSearch() {
    const { searchTerm } = this;
    if (!searchTerm) return;
    this.isLoading = true;
    this.store.search(searchTerm)
      .then(() => this.isLoading = false);
    this.appStore.sendEvent(TREE_EVENT.SEARCH, { searchTerm });
  }

  @action.bound
  handleInfoClick(node) {
    const { id, code } = node;
    if (this.isInfoEnabled) {
      this.appStore.infoCode = code;
    }
    this.handleClose();
    this.appStore.sendEvent(TREE_EVENT.SHOW_INFO, { id, code });
  }

  @action.bound
  async handleNodeClick(node) {
    node.isLoading = true;
    await node.restoreBranch();
    node.isLoading = false;
    this.handleClose();
    if(this.appStore.infoCode !== null)
    {
      this.appStore.infoCode = null;
      this.appStore.sendEvent(TREE_EVENT.CLOSE_INFO);
    }
    const treeNode = this.appStore.dataStore.nodes.get(node.id);
    if (treeNode) {
      this.appStore.scrollToNode(treeNode);
      treeNode.flash();
    }
  }

}

function Transition(props) {
  return <Slide direction="up" {...props} />;
}



// WEBPACK FOOTER //
// ./src/components/SearchDialog.js